import { useEffect, useRef, useState } from "react";
import { Loader2, X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useIsPro } from "@/hooks/useIsPro";
import { useAppSetting } from "@/hooks/useAppSetting";
import { APP_SETTING_KEYS } from "@/hooks/appSettingKeys";
import { usePreferences } from "@/hooks/useUserPreferences";
import { buildPostVerifyPatch } from "@/lib/deliveryChannel";
import { clearRemovedKid } from "@/lib/removedKidLedger";
import { useGoogleCalendar } from "@/hooks/useGoogleCalendar";
import { supabase } from "@/integrations/supabase/client";
import {
  EXTERNAL_SUPABASE_URL,
  EXTERNAL_SUPABASE_PUBLISHABLE_KEY,
} from "@/integrations/external-supabase/client";
import { trackFeature } from "@/lib/analytics";
import { useProSetupFlowOpen, closeProSetupFlow, markProSetupDone } from "@/lib/proSetupFlow";
import { runBackgroundFactCapture } from "@/lib/proSetupBrainDump";
import { isUnverifiedFactWrite, rememberFamilyFact } from "@/lib/rememberFamilyFact";
import {
  RETRYING_LABEL,
  RETRY_LABEL,
  inlineFailureMessage,
  setupFieldLabel,
  retryFailedMessage,
  retrySucceededMessage,
  summaryFailureMessage,
} from "@/lib/proSetupFailureMessage";
import { reportError } from "@/lib/reportError";
import {
  createAppointmentReminders,
  createSizeCheckTask,
  formatShortDateUTC,
  type ReminderInput,
  type LastVisitPill,
} from "@/lib/proSetupTaskCreation";
import { type KidColorRecord } from "@/lib/kidColors";
import { mergeKidNames, kidNamesReceiptLine } from "@/lib/proSetupKidNames";
import { KID_GENDER_FACT_KEY, KID_GENDER_LABELS, KID_GENDER_VALUES, type KidGender } from "@/lib/kidGender";
import {
  PARTNER_RELATION_FACT_KEY,
  PARTNER_RELATION_LABELS,
  PARTNER_RELATION_VALUES,
  type PartnerRelation,
} from "@/lib/partnerRelation";
import { resolveSavedZip } from "@/lib/userLocation";
import { openExternal } from "@/lib/openExternal";
import { FOCUS_AREAS, toStoredOrder } from "@/lib/focusAreas";

const SEND_SMS_URL = `${EXTERNAL_SUPABASE_URL}/functions/v1/send-sms`;

/**
 * Every card in this flow ends by pushing a "✓ …" line into setSetupReceipt.
 * That receipt is the only thing telling her the flow did anything, so it must
 * be evidence of a landed write, never decoration.
 *
 * The trap it kept falling into: supabase-js resolves `{ data, error }` on a
 * DB/RLS failure instead of rejecting. An `await supabase.from(…).insert(…)`
 * whose `error` nobody reads therefore SUCCEEDS as far as the surrounding
 * try/catch is concerned, and the "✓" prints over a row that does not exist.
 * The catch only ever fired on a network-level rejection. #416 fixed exactly
 * this in handleKidNamesNext (see its comment) and handleFocusAreasNext; the
 * remaining six writes never got the same treatment, so a Pro mom could
 * finish onboarding, see a full green checklist, and have Kin hold none of it.
 *
 * The shape now, everywhere in this file: read `error`, throw it, let the
 * existing catch skip the receipt line and report here. Onboarding still never
 * blocks — she keeps moving through the cards either way (that's deliberate,
 * see handleKidNamesNext) — but a failure reaches Sentry → #bugs, so WE find
 * out instead of her, weeks later, when a reminder never arrives.
 * House rule 16: never swallow.
 */
function reportSetupWrite(step: string, e: unknown): void {
  // #1255 — via reportError, which sets the scope BEFORE logging. It used to
  // console.error first, and captureConsoleIntegration turns that into its own
  // Sentry event: the one that actually alerts, arriving with no tags and a
  // title of "[ProSetupFlow] … [object Object]". Per-call tags reach only the
  // captureException copy, so fixes applied here were invisible in #bugs.
  reportError(`[ProSetupFlow] ${step} save failed`, e, {
    tags: { area: "pro-setup-flow", writer: step },
    extra: { code: (e as any)?.code, details: (e as any)?.details, hint: (e as any)?.hint },
  });
}

// v1.3 spec §B: the Pro Setup Flow celebration modal + card wizard. Opened by
// entitlementFlip.ts's openProWelcome() after a confirmed free->pro flip.
// Container decisions (§B.1): centered modal takeover, one card at a time,
// card counter top-right, per-card Skip, localStorage-gated (mirrors
// kin_kids_crew_skipped), incremental writes on Next, in-memory receipt.

type Card =
  | { id: "welcome" }
  | { id: "zip" }
  | { id: "kid_names" }
  | { id: "kid_birthday"; kid: string }
  // #1861 — one per kid with no gender on file, right after her birthday card.
  | { id: "kid_gender"; kid: string }
  | { id: "partner" }
  | { id: "food_rules" }
  | { id: "providers" }
  | { id: "where_days" }
  | { id: "crews_week" }
  | { id: "baby_sizes" }
  | { id: "you" }
  | { id: "freeform" }
  | { id: "sms" }
  | { id: "focus_areas" }
  | { id: "close" };


const NEW_MOM_KEY = "new_mom";

// Priority order per spec §B.13 step 3 — VERIFIED against MOM_TYPES in
// src/constants/chipPools.ts (stored `key` values, not display titles).
const FREEFORM_FRAGMENT_PRIORITY: Array<{ key: string; fragment: string | null }> = [
  { key: "Homestead / DIY Mom", fragment: "your garden zone, the chickens" },
  { key: "Corporate Mom", fragment: "travel weeks, office days" },
  { key: "All-the-Things Mom", fragment: "carpool rotation, the sitter" },
  { key: "Creative / Side Hustle Mom", fragment: "the shop, launch dates" },
  { key: "Just Getting Started Mom", fragment: null },
];
const FREEFORM_FALLBACK_PLACEHOLDER = "the sitter's name, Grandma's birthday, the dog's vet...";
const FREEFORM_NEW_MOM_PLACEHOLDER = "whatever's on your mind, mama...";

function getFreeformPlaceholder(momTypes: string[] | null): string {
  const types = Array.isArray(momTypes) ? momTypes : [];
  if (types.includes(NEW_MOM_KEY)) return FREEFORM_NEW_MOM_PLACEHOLDER;
  const fragments: string[] = [];
  for (const entry of FREEFORM_FRAGMENT_PRIORITY) {
    if (fragments.length >= 3) break;
    if (entry.fragment && types.includes(entry.key)) fragments.push(entry.fragment);
  }
  if (fragments.length === 0) return FREEFORM_FALLBACK_PLACEHOLDER;
  return `${fragments.join(", ")}...`;
}

const DIAPER_SIZES = ["N", "1", "2", "3", "4", "5", "6"];

/** Kid-name rows on the "Who's in your crew?" card (#360). Same ceiling as
 *  KidsCrewSetup.tsx's MAX_KIDS. */
const MAX_KIDS = 6;

const titleStyle: React.CSSProperties = {
  fontFamily: "'DM Serif Display', serif",
  fontSize: 22,
  color: "#1C1A3E",
  marginBottom: 8,
  lineHeight: 1.25,
};
const bodyStyle: React.CSSProperties = {
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontSize: 14,
  color: "#54525C",
  lineHeight: 1.5,
  marginBottom: 18,
};
const labelStyle: React.CSSProperties = {
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontSize: 12,
  fontWeight: 600,
  color: "#54525C",
  marginBottom: 4,
};
const inputStyle: React.CSSProperties = {
  width: "100%",
  border: "1.5px solid #C8C8F4",
  borderRadius: 12,
  padding: "12px 14px",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontSize: 14,
  color: "#1C1A3E",
  background: "#FFFFFF",
  marginBottom: 14,
};
const primaryButtonStyle = (disabled: boolean): React.CSSProperties => ({
  width: "100%",
  height: 48,
  borderRadius: 14,
  backgroundColor: disabled ? "#C8C8F4" : "#5050C8",
  color: "#FFFFFF",
  fontFamily: "'DM Serif Display', serif",
  fontSize: 16,
  border: "none",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.7 : 1,
});
const secondaryButtonStyle: React.CSSProperties = {
  width: "100%",
  height: 44,
  borderRadius: 14,
  backgroundColor: "transparent",
  color: "#54525C",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontSize: 14,
  fontWeight: 600,
  border: "1.5px solid #C8C8F4",
  cursor: "pointer",
  marginTop: 8,
};
const linkButtonStyle: React.CSSProperties = {
  width: "100%",
  background: "none",
  border: "none",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontSize: 13,
  color: "#54525C",
  cursor: "pointer",
  marginTop: 12,
  textAlign: "center",
};

const ProSetupFlowModal = () => {
  const isOpenStore = useProSetupFlowOpen();
  const { user, session, isAdmin, adminPreview } = useAuth();
  const isPro = useIsPro();
  const isProTier = isPro || (isAdmin && adminPreview);
  const { prefs, loading: prefsLoading, updatePreferences, refetchPreferences } = usePreferences();
  const { connected: googleCalConnected } = useGoogleCalendar();
  // SMS opt-in card (Issue #153). Gated behind the same app_settings flag as
  // every other SMS surface — flips true when the Twilio A2P campaign clears.
  const smsLive = useAppSetting(APP_SETTING_KEYS.SMS_LIVE);

  const [visible, setVisible] = useState(false);
  const [ready, setReady] = useState(false);
  // Issue #664 — MUST be declared here, unconditionally, before the
  // `if (...) return null` below. useRef is a hook: declaring it after that
  // early return (where it originally landed) meant the modal called a
  // different number of hooks on the render where `ready` flips true than on
  // the renders before it — React error #310, "rendered more hooks than
  // during the previous render." It only breaks the SECOND time the
  // component progresses past the early return, which is exactly why local
  // vitest/build/tsc never caught it: none of them mount this component
  // through a real ready:false -> true transition. Caught in CI e2e only,
  // as a React error boundary fallback ("Something went wrong. Please
  // refresh the page.") on every Pro-fixture test in this file.
  const cardTokenRef = useRef(0);
  const [cards, setCards] = useState<Card[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  // #673 — a receipt line optionally carries the id of the scheduled task it
  // reports on, so the Close card's primary button can deep-link to it. Lines
  // with no taskId (prefs/family_context writes) render exactly as before.
  const [setupReceipt, setSetupReceipt] = useState<{ text: string; taskId?: string }[]>([]);
  // #673 — the FIRST task created this session, so "the task Kin just made"
  // is unambiguous even if the flow ends up creating more than one (e.g. a
  // dentist reminder AND a size check-in share one Close card).
  const [firstCreatedTaskId, setFirstCreatedTaskId] = useState<string | null>(null);
  const noteCreatedTask = (taskId: string) => {
    if (!taskId) return;
    setFirstCreatedTaskId((prev) => prev ?? taskId);
  };
  const [saving, setSaving] = useState(false);
  // Issue #665 — the user-visibility half of #416/#541's reportSetupWrite.
  // #541 made every write in this flow report to Sentry on failure; nothing
  // told the mom herself. `anyWriteFailed` persists for the whole session
  // (drives the Close-card summary line). `failureNoticeStep` is scoped to
  // exactly one card: set to the index the flow is about to land on right as
  // a write fails, so `failureNoticeStep === stepIndex` is true for only
  // that one card and stops matching the moment she advances again — no
  // extra "seen" bookkeeping needed for it to expire on its own.
  const [failureNoticeStep, setFailureNoticeStep] = useState<number | null>(null);
  // #1469 — WHICH writes failed, in the order they failed, deduped. Drives both
  // notices. Step keys, not labels: the lookup happens at render so the wording
  // can change without touching this state.
  //
  // #1478 — each entry now carries the write itself, so she can tap it again.
  //
  // ⚠️ `retry` re-runs ONLY that one write, never the handler around it. The
  // handlers do other things on success — splicing birthday cards in, seeding
  // babySizeKid, advancing the flow — and re-running those from a card she has
  // long since left would move the flow under her.
  //
  // ⚠️ `receipt` is held here rather than printed by the retry, because the
  // receipt line and the write are two different things: the write is what
  // must not be duplicated, the receipt is what she reads on the Close card.
  // A retry that saved without printing the line would leave the Close card
  // still saying nothing landed (house rule 13).
  //
  // ⚠️ `retry: null` means this step's write cannot safely be re-run — see
  // RETRY_UNSAFE_STEPS in proSetupFailureMessage.ts. It keeps the named
  // message and loses the button.
  // #1857 — `unverified` is the helper's third state: the response was lost
  // and the read-back could not answer. The notice then says "couldn't
  // confirm" rather than "didn't save", and the retry (an idempotent upsert)
  // is the thing that settles it.
  type FailedStep = { step: string; retry: (() => Promise<void>) | null; receipt: string | null; unverified: boolean };
  const [failedSteps, setFailedSteps] = useState<FailedStep[]>([]);
  // ⚠️ Derived, not its own boolean. It used to be `anyWriteFailed`, set to
  // true and never cleared, which was correct while nothing could un-fail —
  // now a successful retry has to be able to take the summary line away, and a
  // second source of truth is how those two drift.
  const anyWriteFailed = failedSteps.length > 0;
  // The retry currently in flight, and how the last one ended. `retryOutcome`
  // is what makes the tap visible: without it a retry that worked and a retry
  // that did nothing look identical, which is the failure this issue is about.
  const [retryingStep, setRetryingStep] = useState<string | null>(null);
  const [retryOutcome, setRetryOutcome] = useState<{ step: string; landed: boolean; unverified?: boolean } | null>(null);
  // The one the inline notice is about: the most recent failure, so a one-line
  // notice never accumulates stale names.
  const [lastFailedStep, setLastFailedStep] = useState<string | null>(null);
  const [knownKids, setKnownKids] = useState<string[]>([]);
  const [freeformPlaceholder, setFreeformPlaceholder] = useState(FREEFORM_FALLBACK_PLACEHOLDER);

  const [zip, setZip] = useState("");
  const [kidNameInputs, setKidNameInputs] = useState<string[]>(["", ""]);
  const [kidBirthdayValues, setKidBirthdayValues] = useState<Record<string, string>>({});
  // #1861 — a kid absent from this map has not been answered; that is a real
  // state and Next on an unanswered card writes nothing.
  const [kidGenderValues, setKidGenderValues] = useState<Record<string, KidGender>>({});
  const [partnerName, setPartnerName] = useState("");
  // #1861 — the word she uses for them. null = not answered, never a default.
  const [partnerRelation, setPartnerRelation] = useState<PartnerRelation | null>(null);
  const [foodRulesText, setFoodRulesText] = useState("");
  const [pediatricianName, setPediatricianName] = useState("");
  const [dentistName, setDentistName] = useState("");
  const [pediatricianReminder, setPediatricianReminder] = useState(true);
  const [dentistReminder, setDentistReminder] = useState(true);
  // #672 — optional, per-provider. null keeps the original "just seen" behavior.
  const [pediatricianLastVisit, setPediatricianLastVisit] = useState<LastVisitPill | null>(null);
  const [dentistLastVisit, setDentistLastVisit] = useState<LastVisitPill | null>(null);
  const [whereDaysText, setWhereDaysText] = useState("");
  const [crewsWeekText, setCrewsWeekText] = useState("");
  const [babySizeKid, setBabySizeKid] = useState<string | null>(null);
  const [diaperSize, setDiaperSize] = useState<string | null>(null);
  const [clothingSize, setClothingSize] = useState("");
  const [sizeCheckReminder, setSizeCheckReminder] = useState(true);
  const [aboutMeText, setAboutMeText] = useState("");
  const [freeformText, setFreeformText] = useState("");
  const [focusAreas, setFocusAreas] = useState<string[]>([]);

  // SMS card state (§B card, two slots in one position: phone entry -> code).
  const [smsStep, setSmsStep] = useState<"phone" | "code">("phone");
  const [smsPhoneDigits, setSmsPhoneDigits] = useState(""); // raw 10-digit US input
  const [smsCode, setSmsCode] = useState("");
  const [smsSubmitting, setSmsSubmitting] = useState(false);
  const [smsError, setSmsError] = useState<string | null>(null);
  // A2P 10DLC (error 30923, Forced Consent Violation): consent is its own
  // explicit, unchecked-by-default action — a phone number is not agreement.
  // Same gate as SmsSetupSheet; this card is the second opt-in path. Never
  // seed this true.
  const [smsConsented, setSmsConsented] = useState(false);

  // Build the card list once when the modal opens: computes conditional
  // cards (§B.5 kid birthdays, §B.11 baby sizes) and resets all form state.
  useEffect(() => {
    if (!isOpenStore || !isProTier || prefsLoading || !user) return;
    let cancelled = false;
    (async () => {
      const knownKidNames = prefs.known_kid_names.filter(
        (n) => typeof n === "string" && n.trim().length > 0
      );

      let kidsNeedingBirthday: string[] = [];
      let kidsNeedingGender: string[] = [];
      if (knownKidNames.length > 0) {
        // #1861 — ONE read for both identity facts, split by key below. `key`
        // joins the select list so the split is possible; without it every row
        // would look like a birthday and no gender card would ever render — the
        // explicit-select trap, in the direction that hides a feature.
        const { data } = await (supabase as any)
          .from("family_context")
          .select("subject, key")
          .eq("user_id", user.id)
          .eq("category", "kid")
          .in("key", ["birthdate", KID_GENDER_FACT_KEY])
          .eq("archived", false)
          .in("subject", knownKidNames);
        const rows = (data || []) as Array<{ subject: string; key: string }>;
        const haveBirthday = new Set(rows.filter((r) => r.key === "birthdate").map((r) => r.subject));
        const haveGender = new Set(rows.filter((r) => r.key === KID_GENDER_FACT_KEY).map((r) => r.subject));
        kidsNeedingBirthday = knownKidNames.filter((k) => !haveBirthday.has(k));
        kidsNeedingGender = knownKidNames.filter((k) => !haveGender.has(k));
      }
      if (cancelled) return;

      const isNewMom = Array.isArray(prefs.mom_types) && prefs.mom_types.includes(NEW_MOM_KEY);

      // SMS card renders ONLY when the flag is live AND the user has no verified
      // phone yet (§B — additive card between Freeform and Close). Free tier is
      // already excluded because the whole modal is Pro-gated; no second gate.
      const showSms = smsLive && !prefs.phone_verified_at;

      // "Who's in your crew?" (#360) renders ONLY when Kin knows zero kids —
      // i.e. she skipped Kids Crew Setup at signup. It sits BEFORE the birthday
      // cards; saving it splices a birthday card in for each kid it adds (see
      // handleKidNamesNext).
      const showKidNames = knownKidNames.length === 0;

      const nextCards: Card[] = [
        { id: "welcome" },
        { id: "zip" },
        ...(showKidNames ? [{ id: "kid_names" as const }] : []),
        ...kidsNeedingBirthday.map((kid) => ({ id: "kid_birthday" as const, kid })),
        // #1861 — every account from before Oct 20 2026 has no gender set, and
        // this flow is the one built to fill exactly that kind of gap.
        ...kidsNeedingGender.map((kid) => ({ id: "kid_gender" as const, kid })),
        { id: "partner" },
        { id: "food_rules" },
        { id: "providers" },
        { id: "where_days" },
        { id: "crews_week" },
        ...(isNewMom ? [{ id: "baby_sizes" as const }] : []),
        { id: "you" },
        { id: "freeform" },
        ...(showSms ? [{ id: "sms" as const }] : []),
        { id: "focus_areas" },
        { id: "close" },
      ];

      setCards(nextCards);
      setKnownKids(knownKidNames);
      setFreeformPlaceholder(getFreeformPlaceholder(prefs.mom_types));
      setStepIndex(0);
      setSetupReceipt([]);
      setFirstCreatedTaskId(null);
      // ⚠️ #1478 — the failure state has to be cleared on every open, and this
      // is new. It was survivable before: a replayed setup showing last
      // session'''s "About you didn'''t save" was stale and confusing but inert.
      // It is not inert now. Every entry carries a CLOSURE over the values she
      // typed last time, so a stale one would let her tap Retry on this run'''s
      // Close card and write last run'''s answer. Settings -> Replay Pro setup
      // makes that reachable on any Pro account.
      setFailedSteps([]);
      setLastFailedStep(null);
      setFailureNoticeStep(null);
      setRetryOutcome(null);
      setRetryingStep(null);
      // Seed from whatever she's already told Kin — Settings' zip row, or the
      // Morning Notes weather zip (#533). Asking a mom for her zip a second
      // time when it's already on file is the whole complaint in this issue.
      setZip(resolveSavedZip(prefs) ?? "");
      setKidNameInputs(["", ""]);
      setKidBirthdayValues({});
      // #1861 — a replay must not carry a pick from the last run into a card
      // she has not seen yet; both reset with the rest of the form.
      setKidGenderValues({});
      setPartnerName("");
      setPartnerRelation(null);
      setFoodRulesText("");
      setPediatricianName("");
      setDentistName("");
      setPediatricianReminder(true);
      setDentistReminder(true);
      setPediatricianLastVisit(null);
      setDentistLastVisit(null);
      setWhereDaysText("");
      setCrewsWeekText("");
      setBabySizeKid(knownKidNames.length === 1 ? knownKidNames[0] : null);
      setDiaperSize(null);
      setClothingSize("");
      setSizeCheckReminder(true);
      setAboutMeText("");
      setFreeformText("");
      setSmsStep("phone");
      setSmsPhoneDigits("");
      setSmsCode("");
      setSmsSubmitting(false);
      setSmsError(null);
      setSmsConsented(false);
      setReady(true);
      requestAnimationFrame(() => setVisible(true));
    })();
    return () => {
      cancelled = true;
    };
    // Only re-run when the modal opens for a resolved pro user — not on
    // every prefs/context re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenStore, isProTier, prefsLoading, user?.id, smsLive]);

  if (!isOpenStore || !isProTier || !ready) return null;

  const currentCard = cards[stepIndex];
  const animateOut = (cb: () => void) => {
    setVisible(false);
    setTimeout(cb, 300);
  };
  // Issue #664 — cardTokenRef (declared near the top of the component, see
  // that comment for why) is bumped every time the visible card changes for
  // ANY reason (a Skip tap or a handler's own successful/failed advance).
  // Each async save handler captures this value before awaiting; a mismatch
  // afterward means she already skipped past that card while the write was
  // still in flight. That in-flight write is left to finish on its own
  // (nothing here cancels it — proSetupBrainDump.ts and the DB calls are
  // out of scope for this issue) but its result is ignored: no receipt line
  // for a card she never saw finish, and no second goNext() call, which
  // would otherwise silently skip an extra card on top of the one she chose
  // to skip.
  const goNext = () => {
    cardTokenRef.current += 1;
    setStepIndex((i) => Math.min(i + 1, cards.length - 1));
  };
  // Issue #665 — call alongside (never instead of) reportSetupWrite() at
  // every write-failure site. Kept separate from reportSetupWrite itself:
  // that function is intentionally a plain top-level function with no access
  // to component state, and the issue asks explicitly to leave its Sentry
  // reporting unchanged. Computes the target step the same way goNext() will
  // — the actual advance always happens right after this is called.
  //
  // #1478 — `retry` re-runs ONLY this write and throws on failure, so the first
  // attempt and the retry are the same code. Pass `null` when re-running it
  // would not be safe.
  const noteWriteFailure = (
    step: string,
    retry: (() => Promise<void>) | null = null,
    receipt: string | null = null,
    unverified = false,
  ) => {
    setFailureNoticeStep(Math.min(stepIndex + 1, cards.length - 1));
    // #1469 — the same step key already passed to reportSetupWrite, so the two
    // can never drift. Deduped: a card that fails twice is one thing she is
    // missing, not two. #1478 keeps the FIRST entry's closure on a repeat: it
    // is the one whose values she actually typed on that card.
    setFailedSteps((prev) => {
      const existing = prev.find((f) => f.step === step);
      if (!existing) return [...prev, { step, retry, receipt, unverified }];
      // ⚠️ One step can fail twice for two different reasons — baby_sizes is
      // both the size FACTS (retryable) and the size-check REMINDER (not). If
      // either half cannot be re-run, the whole step loses its button: a retry
      // that fixes half of what she is missing and then says "saved 💜" is a
      // worse lie than no button at all.
      if (existing.retry && retry === null) {
        return prev.map((f) => (f.step === step ? { ...f, retry: null, receipt: null, unverified } : f));
      }
      // #1857 — the LATEST outcome decides the wording: a step that was
      // unverified and then provably failed is a failure, and vice versa.
      return prev.map((f) => (f.step === step ? { ...f, unverified } : f));
    });
    setLastFailedStep(step);
    // A fresh failure clears a stale outcome, so last time's "saved 💜" never
    // sits above this time's failure.
    setRetryOutcome(null);
  };

  /**
   * #1478 — run one failed write again, and say what happened either way.
   *
   * ⚠️ The whole point is the outcome, not the attempt. A tap that silently
   * does nothing is worse than the vague message #1469 replaced (house rule 13,
   * and house rule 16 one level up: the retry's result is READ before anything
   * is said about it). So every path here ends in either the step leaving
   * `failedSteps` with its receipt line printed, or a message saying it still
   * did not save.
   *
   * ⚠️ Serialised on `retryingStep`. Two taps on one button is the ordinary way
   * a phone user produces two concurrent writes, and these land on
   * family_context rows under a unique index — the exact 23505 race #1483 and
   * #1484 are about.
   */
  const runRetry = async (entry: FailedStep) => {
    if (!entry.retry || retryingStep) return;
    setRetryingStep(entry.step);
    setRetryOutcome(null);
    try {
      await entry.retry();
      setFailedSteps((prev) => prev.filter((f) => f.step !== entry.step));
      if (entry.receipt) setSetupReceipt((prev) => [...prev, { text: entry.receipt as string }]);
      setRetryOutcome({ step: entry.step, landed: true });
    } catch (e) {
      // Same channel as the first attempt, so a step that fails twice shows as
      // two events rather than one — the second one is the interesting one.
      reportSetupWrite(entry.step, e);
      setRetryOutcome({ step: entry.step, landed: false, unverified: isUnverifiedFactWrite(e) });
    } finally {
      setRetryingStep(null);
    }
  };
  // #673 — highlightTaskId is optional and additive to the existing
  // kin:navigate-tab detail shape (shared by MorningNotesCard, SavedToast,
  // useRealtimeVoice). Omitted entirely (not just undefined) when there's no
  // task to point at, so a setup run that created nothing behaves exactly as
  // it always has.
  const finishFlow = (destination: "chat" | "tasks" | null, highlightTaskId?: string) => {
    markProSetupDone();
    if (destination) {
      window.dispatchEvent(
        new CustomEvent("kin:navigate-tab", {
          detail: highlightTaskId ? { tab: destination, highlightTaskId } : { tab: destination },
        })
      );
    }
    animateOut(() => {
      closeProSetupFlow();
      setReady(false);
    });
  };

  const handleWelcomeNext = () => goNext();
  const handleFullSkip = () => finishFlow(null);

  const handleZipNext = async () => {
    if (saving || !user) return;
    const trimmed = zip.trim();
    if (/^\d{5}$/.test(trimmed)) {
      const myToken = cardTokenRef.current;
      setSaving(true);
      // Through updatePreferences, not a bare upsert — same reason as the kid
      // names below. As of #533 this zip is the canonical location every card
      // prefills from, so the shared prefs context has to update in place or
      // Event Finder keeps asking her for a city she just typed one screen ago.
      // #1478 — ONE named write, called by the first attempt and by the retry.
      // Extracted rather than copied: two copies of a write is how a retry
      // quietly stops matching the thing it is retrying.
      const writeZip = async () => {
        const err = await updatePreferences({ zip_code: trimmed });
        if (err) throw err;
      };
      const zipReceipt = `✓ Local suggestions set to ${trimmed}`;
      let zipError: unknown = null;
      try {
        await writeZip();
      } catch (e) {
        zipError = e;
      }
      setSaving(false);
      if (cardTokenRef.current !== myToken) return; // #664 — skipped mid-save
      if (zipError) {
        reportSetupWrite("zip", zipError);
        noteWriteFailure("zip", writeZip, zipReceipt);
      } else {
        setSetupReceipt((prev) => [...prev, { text: zipReceipt }]);
      }
    }
    goNext();
  };

  // "Who's in your crew?" (#360). Writes to the SAME columns Kids Crew Setup
  // writes (user_preferences.known_kid_names + known_kid_colors) so Tasks-tab
  // entity resolution, chat context, Family Profile and the birthday guard all
  // behave identically regardless of which path created the kid. Goes through
  // updatePreferences (not a bare upsert) so the shared prefs context updates
  // in place and those surfaces re-render without a reload.
  const handleKidNamesNext = async () => {
    if (saving || !user) return;
    const { names, colors, added } = mergeKidNames(
      prefs.known_kid_names,
      (prefs.known_kid_colors as KidColorRecord) || {},
      kidNameInputs
    );
    if (added.length === 0) {
      // Nothing new typed (empty, or every name already known). Same as Skip.
      goNext();
      return;
    }

    const myToken = cardTokenRef.current;
    setSaving(true);
    // Saved, not just attempted. updatePreferences returns the PostgrestError
    // rather than throwing (#416) — supabase-js resolves { data, error } on a
    // DB/RLS failure, so the catch below only ever fires on a network-level
    // rejection. Without checking the returned value we'd tell her "✓ saved"
    // and then ask for birthdays of kids that never persisted.
    let saved = false;
    // #1478 — hoisted above the try so the outer catch can hand it to
    // noteWriteFailure too. Re-runs the preference write and nothing else: the
    // card splice, setKnownKids and the babySizeKid seed below are flow
    // effects, and re-running those from the Close card would move the flow
    // under her.
    const writeKidNames = async () => {
      const e = await updatePreferences({ known_kid_names: names, known_kid_colors: colors as any });
      if (e) throw e;
    };
    const kidNamesReceipt = kidNamesReceiptLine(added);
    try {
      let err: unknown = null;
      try {
        await writeKidNames();
      } catch (e) {
        err = e;
      }
      // #1243 — Pro setup is the other EXPLICIT place she names her children,
      // so it clears the removed-kid ledger too. Without this, a mom who
      // removed a kid and later re-ran setup would have Kin still refusing to
      // treat that child as hers, with nothing on screen explaining why.
      //
      // Only when the write actually landed (`!err`), and never fatal: the kid
      // names DID save, and failing this must not turn a successful setup card
      // into an error. Reported through the same channel as the write itself.
      if (!err && user) {
        for (const kidName of names) {
          try {
            await clearRemovedKid(supabase, user.id, kidName);
          } catch (e) {
            reportSetupWrite("removed_kid_ledger", e as any);
          }
        }
      }
      // #664 — only touch receipt/cards/state if she hasn't already skipped
      // past this card while the write was in flight. An abandoned write can
      // still land in the database (nothing here cancels the request), but
      // it must not splice in birthday cards or a receipt line for a flow
      // position she's already moved past.
      if (cardTokenRef.current === myToken) {
        if (err) {
          reportSetupWrite("kid_names", err);
          noteWriteFailure("kid_names", writeKidNames, kidNamesReceipt);
        } else {
          saved = true;
          setSetupReceipt((prev) => [...prev, { text: kidNamesReceipt }]);
          setKnownKids(names);
          // The card list was computed at modal open, when there were no kids to
          // ask birthdays for. Splice one birthday card per newly added kid in
          // right after this card so step 4 of the flow still runs for them.
          setCards((prev) => {
            const idx = prev.findIndex((c) => c.id === "kid_names");
            if (idx === -1) return prev;
            const birthdayCards: Card[] = added.map((kid) => ({ id: "kid_birthday" as const, kid }));
            // #1861 — a kid added here has no gender either; ask right after.
            const genderCards: Card[] = added.map((kid) => ({ id: "kid_gender" as const, kid }));
            return [...prev.slice(0, idx + 1), ...birthdayCards, ...genderCards, ...prev.slice(idx + 1)];
          });
          // Baby Sizes resolves its subject automatically when there's exactly one
          // kid (spec §B.11); it had none to resolve when the list was built.
          if (names.length === 1) setBabySizeKid(names[0]);
        }
      }
    } catch (e) {
      reportSetupWrite("kid_names", e);
      noteWriteFailure("kid_names", writeKidNames, kidNamesReceipt);
    }
    setSaving(false);
    if (cardTokenRef.current !== myToken) return; // #664 — skipped mid-save, already advanced
    // Advance either way — every other handler in this flow does, and trapping
    // her on a card she can't get past is worse than a lost preference.
    if (saved) {
      // Advance without goNext's clamp: `cards` in this closure is the pre-splice
      // list, so its length would be stale. The spliced list is always longer, so
      // i + 1 is always in range.
      cardTokenRef.current += 1;
      setStepIndex((i) => i + 1);
    } else {
      // Nothing was spliced, so the closure's `cards` length is accurate here
      // and goNext's clamp is the right guard.
      goNext();
    }
  };

  // Preference capture ONLY — this writes user_preferences.focus_areas and
  // creates NOTHING else. Deliberate: a mom who just paid must not discover
  // helpers she never set up (July 23 ruling, #361 guardrails). All this
  // changes is what the "All of Kin's helpers" sheet shows her first.
  const handleFocusAreasNext = async () => {
    if (saving || !user) return;
    if (focusAreas.length === 0) {
      goNext();
      return;
    }
    const myToken = cardTokenRef.current;
    setSaving(true);
    // Catalog order, not tap order — see toStoredOrder(). Tapping pills isn't
    // ranking them, and a sheet that reshuffles between sessions is worse than
    // one that's simply stable.
    const ordered = toStoredOrder(focusAreas);
    const writeFocusAreas = async () => {
      const e = await updatePreferences({ focus_areas: ordered } as any);
      if (e) throw e;
    };
    const focusLabels = FOCUS_AREAS.filter((f) => ordered.includes(f.key)).map((f) => f.label);
    const focusReceipt = `✓ Kin's watching ${focusLabels.join(", ")}`;
    let focusError: unknown = null;
    try {
      await writeFocusAreas();
    } catch (e) {
      focusError = e;
    }
    setSaving(false);
    if (cardTokenRef.current !== myToken) return; // #664 — skipped mid-save
    if (focusError) {
      reportSetupWrite("focus_areas", focusError);
      noteWriteFailure("focus_areas", writeFocusAreas, focusReceipt);
    } else {
      setSetupReceipt((prev) => [...prev, { text: focusReceipt }]);
    }
    goNext();
  };

  const handleKidBirthdayNext = async (kid: string) => {
    if (saving || !user) return;
    const val = kidBirthdayValues[kid] || "";
    if (val) {
      const myToken = cardTokenRef.current;
      setSaving(true);
      // #1478 — ONE named write, shared by the first attempt and the retry.
      // Extracted rather than copied: two copies of a write is how a retry
      // quietly stops matching the thing it is retrying.
      const writeBirthday = async () => {
        // #1479 — was a bare .insert(), the same 23505 #1469 fixed for about_me
        // and partner_name. `birthdate` is named in
        // 20260810000000_family_context_allow_multivalued_keys.sql as a key that
        // STAYS single-valued, so a second pass over this card collides and the
        // row that survives is the OLD one: her correction is what gets dropped.
        //
        // NOT fixable with .upsert({ onConflict }) — expression index
        // (coalesce(subject,'')) with a partial WHERE, so PostgREST cannot infer
        // it and Postgres answers 42P10. See rememberFamilyFact.ts.
        const result = await rememberFamilyFact(
          { category: "kid", subject: kid, key: "birthdate", value: val },
          "pro_onboarding",
          "high",
        );
        // `ok` is the only honest success signal: the RPC reports refusals as a
        // returned action with no Postgres error, so checking `error` alone
        // would print the receipt over a write that never landed (house rule 16).
        if (!result.ok) {
          throw Object.assign(
            new Error(
              `upsert_family_fact returned ${result.action ?? "no action row"}${result.error ? `: ${result.error}` : ""}`,
            ),
            { code: result.action ?? "no_action_row", details: result.error },
          );
        }
      };
      const birthdayReceipt = `✓ ${kid}'s birthday saved`;
      try {
        await writeBirthday();
        if (cardTokenRef.current === myToken) {
          setSetupReceipt((prev) => [...prev, { text: birthdayReceipt }]);
        }
      } catch (e) {
        reportSetupWrite("kid_birthday", e);
        // Safe to re-run: upsert_family_fact resolves the whole write in one
        // statement, so a second attempt after a partial success overwrites the
        // same row rather than making another.
        noteWriteFailure("kid_birthday", writeBirthday, birthdayReceipt, isUnverifiedFactWrite(e));
      }
      setSaving(false);
      if (cardTokenRef.current !== myToken) return; // #664 — skipped mid-save
    }
    goNext();
  };

  // #1861 — mirrors handleKidBirthdayNext exactly: one named write shared by
  // the first attempt and the retry (#1478), `ok` as the only success signal
  // (house rule 16), receipt only when the write landed, advance either way.
  const handleKidGenderNext = async (kid: string) => {
    if (saving || !user) return;
    const val = kidGenderValues[kid] ?? null;
    if (val) {
      const myToken = cardTokenRef.current;
      setSaving(true);
      const writeGender = async () => {
        // `gender` is single-valued by omission from the #747 multi-valued set,
        // so a replay overwrites the one live row — no 23505 on a second pass.
        const result = await rememberFamilyFact(
          { category: "kid", subject: kid, key: KID_GENDER_FACT_KEY, value: val },
          "pro_onboarding",
          "high",
        );
        if (!result.ok) {
          throw Object.assign(
            new Error(
              `upsert_family_fact returned ${result.action ?? "no action row"}${result.error ? `: ${result.error}` : ""}`,
            ),
            { code: result.action ?? "no_action_row", details: result.error },
          );
        }
      };
      const genderReceipt = `✓ Kin knows ${kid} is a ${val}`;
      try {
        await writeGender();
        if (cardTokenRef.current === myToken) {
          setSetupReceipt((prev) => [...prev, { text: genderReceipt }]);
        }
      } catch (e) {
        reportSetupWrite("kid_gender", e);
        noteWriteFailure("kid_gender", writeGender, genderReceipt, isUnverifiedFactWrite(e));
      }
      setSaving(false);
      if (cardTokenRef.current !== myToken) return; // #664 — skipped mid-save
    }
    goNext();
  };

  const handlePartnerNext = async () => {
    if (saving || !user) return;
    const name = partnerName.trim();
    if (name) {
      const myToken = cardTokenRef.current;
      setSaving(true);
      // #1478 — ONE named write, shared by the first attempt and the retry.
      // Extracted rather than copied: two copies of a write is how a retry
      // quietly stops matching the thing it is retrying.
      const writePartner = async () => {
        // Was a bare .insert(), which threw 23505 on every re-entry —
        // family_context_fact_uidx is unique on
        // (user_id, category, coalesce(subject,''), key) where archived = false,
        // and the partner card is unconditional: Settings → "Replay Pro setup"
        // (any Pro account since Aug 5 2026) and a second device both replay it
        // from card 0 with the input blank, so she retypes a name that is
        // already stored. Sentry JAVASCRIPT-REACT-10.
        //
        // NOT fixable with .upsert({ onConflict }): that index is an EXPRESSION
        // index (coalesce(subject,''), not a bare column) with a partial WHERE,
        // and PostgREST's on_conflict takes a plain column list, so Postgres
        // cannot infer it and answers 42P10. Same reason useBabysitterMemory.ts
        // and TasksTab.upsertSizeFact avoid onConflict on this table.
        //
        // upsert_family_fact() resolves it server-side in one statement:
        // partner_name is not in the #747 multi-valued set, so it overwrites the
        // one live row and keeps the old name in metadata.previous_value. Atomic
        // where a client-side select-then-write would still race into 23505.
        const result = await rememberFamilyFact(
          { category: "household", subject: null, key: "partner_name", value: name },
          "pro_onboarding",
          "high",
        );
        // The RPC reports refusals as a returned `action`, not a Postgres error,
        // so `ok` is the only honest success signal — checking `error` alone
        // would print the "✓ Kin knows about ..." receipt over a write that
        // never landed.
        if (!result.ok) {
          // reportSetupWrite() lifts `code`/`details`/`hint` off the thrown
          // value into Sentry's `extra`. A bare Error carries none of them, so
          // this failure would arrive in Sentry LESS triageable than the
          // PostgrestError it replaced — and the RPC's refusal reason is the
          // whole diagnosis here, since `rejected_*` is a validation refusal
          // rather than anything the transport can tell you about.
          throw Object.assign(
            new Error(
              `upsert_family_fact returned ${result.action ?? "no action row"}${result.error ? `: ${result.error}` : ""}`,
            ),
            { code: result.action ?? "no_action_row", details: result.error },
          );
        }
        // #1861 — the word she used for them, INSIDE the same named write so a
        // retry re-runs both. Both facts are single-valued overwrites, so a
        // retry after the name landed and the relation did not is safe: the
        // name is rewritten to itself. Only when she picked one — a partner
        // with no relation stays "they", never a default.
        if (partnerRelation) {
          // Block-scoped `result` on purpose: proSetupReplayTwice.test.ts
          // (#1479) counts `if (!result.ok)` guards against rememberFamilyFact
          // calls, and a differently-named binding would read to it as an
          // ungated write. The shadow is the honest spelling of the same gate.
          const result = await rememberFamilyFact(
            { category: "household", subject: null, key: PARTNER_RELATION_FACT_KEY, value: partnerRelation },
            "pro_onboarding",
            "high",
          );
          if (!result.ok) {
            throw Object.assign(
              new Error(
                `upsert_family_fact (partner_relation) returned ${result.action ?? "no action row"}${result.error ? `: ${result.error}` : ""}`,
              ),
              { code: result.action ?? "no_action_row", details: result.error },
            );
          }
        }
      };
      const partnerReceipt = partnerRelation
        ? `✓ Kin knows about ${name}, your ${partnerRelation}`
        : `✓ Kin knows about ${name}`;
      try {
        await writePartner();
        if (cardTokenRef.current === myToken) {
          setSetupReceipt((prev) => [...prev, { text: partnerReceipt }]);
        }
      } catch (e) {
        reportSetupWrite("partner", e);
        // Safe to re-run: partner_name is single-valued and the RPC resolves it
        // in one statement, so a second attempt overwrites the same row.
        noteWriteFailure("partner", writePartner, partnerReceipt, isUnverifiedFactWrite(e));
      }
      setSaving(false);
      if (cardTokenRef.current !== myToken) return; // #664 — skipped mid-save
    }
    goNext();
  };

  // Shared handler for the 3 Brain Dump cards (§B.7/§B.9/§B.10) — see
  // src/lib/proSetupBrainDump.ts for why this calls kin-chat directly.
  const handleBrainDumpNext = async (text: string, receiptLine: string) => {
    if (saving) return;
    const trimmed = text.trim();
    if (trimmed && user && session?.access_token) {
      const myToken = cardTokenRef.current;
      setSaving(true);
      // #664 — this call is NOT cancelled on Skip (proSetupBrainDump.ts and
      // its timing are out of scope for this issue). It keeps running in the
      // background; only its result is ignored below if she's already moved
      // on, so a card she skipped can't retroactively grow a receipt line or
      // silently skip an extra card via a second goNext().
      const { factsCaptured } = await runBackgroundFactCapture({
        text: trimmed,
        userId: user.id,
        accessToken: session.access_token,
        googleCalConnected,
      });
      setSaving(false);
      if (cardTokenRef.current !== myToken) return; // skipped mid-save
      if (factsCaptured > 0) {
        setSetupReceipt((prev) => [...prev, { text: receiptLine }]);
      }
    }
    goNext();
  };

  const handleProvidersNext = async () => {
    if (saving || !user) return;
    const myToken = cardTokenRef.current;
    setSaving(true);
    // #1528 — hoisted so the catch can build a retry that re-runs ONLY what did
    // not land. The providers card has TWO writes with different retry safety:
    // the family_providers insert is a bare insert with no uniqueness behind it,
    // and createAppointmentReminders is idempotent as of #1528. Re-running both
    // blindly would duplicate her providers to fix her reminders.
    const rows: any[] = [];
    const reminders: ReminderInput[] = [];
    let providerRowsLanded = false;
    try {
      if (pediatricianName.trim()) {
        rows.push({
          user_id: user.id,
          provider_name: pediatricianName.trim(),
          type: "pediatrician",
          cadence_months: 12,
          source: "pro_onboarding",
          confidence: "high",
        });
      }
      if (dentistName.trim()) {
        rows.push({
          user_id: user.id,
          provider_name: dentistName.trim(),
          type: "dentist",
          cadence_months: 6,
          source: "pro_onboarding",
          confidence: "high",
        });
      }
      if (rows.length > 0) {
        const { error } = await (supabase as any).from("family_providers").insert(rows);
        if (error) throw error;
      }
      // Past this point the provider rows exist (or there were none to write),
      // so a retry must not insert them again.
      providerRowsLanded = true;

      // Write 2 (spec §B.8): one scheduled_tasks reminder per checked box
      // with a non-empty matching name field.
      //
      // #672 — this card never asks WHICH kid a provider is for (issue #672's
      // "the family_providers row already knows which kid" premise doesn't
      // hold — that insert above carries no kid field at all). Rather than
      // adding a kid picker here (bigger UI than this card's stated small
      // scope), a single-kid family gets real attribution for free — there's
      // only one kid it could be. A multi-kid family keeps "the crew", same
      // as before this issue, since guessing wrong is worse than staying
      // generic.
      const inferredKid = knownKids.length === 1 ? knownKids[0] : undefined;
      if (dentistReminder && dentistName.trim()) {
        reminders.push({
          category: "dentist",
          label: "Dentist",
          providerName: dentistName.trim(),
          cadenceMonths: 6,
          lastVisitPill: dentistLastVisit ?? undefined,
          kidName: inferredKid,
        });
      }
      if (pediatricianReminder && pediatricianName.trim()) {
        reminders.push({
          category: "doctor",
          label: "Doctor",
          providerName: pediatricianName.trim(),
          cadenceMonths: 12,
          lastVisitPill: pediatricianLastVisit ?? undefined,
          kidName: inferredKid,
        });
      }
      if (reminders.length > 0) {
        // #673 — the receipt names each reminder with the real date its
        // first nudge fires, and the card's primary button deep-links to it.
        const { taskId, reminders: created } = await createAppointmentReminders(user.id, reminders);
        if (cardTokenRef.current === myToken) {
          const lines = created.map((r) => ({
            text: `✓ ${r.label} reminder — first nudge ${formatShortDateUTC(r.nudgeDateIso)}`,
            taskId,
          }));
          setSetupReceipt((prev) => [...prev, ...lines]);
          noteCreatedTask(taskId);
        }
      }
      // #1528 — this card now has a retry. #1478 could not offer one because
      // createAppointmentReminders() APPENDED: a second tap gave her the same
      // dentist twice in one tracker with nothing to tell them apart. It now
      // merges on (category, provider name), so re-running it is safe.
    } catch (e) {
      reportSetupWrite("providers", e);
      // ⚠️ Re-runs only what did NOT land. If the family_providers insert
      // already succeeded and the reminder write is what failed, retrying that
      // insert would give her two of every provider — the same duplicate this
      // issue exists to prevent, one table over. `providerRowsLanded` is the
      // only thing separating those two cases.
      const retryRows = providerRowsLanded ? [] : [...rows];
      const retryReminders = [...reminders];
      noteWriteFailure(
        "providers",
        async () => {
          if (retryRows.length > 0) {
            const { error } = await (supabase as any).from("family_providers").insert(retryRows);
            if (error) throw error;
          }
          if (retryReminders.length > 0) {
            await createAppointmentReminders(user.id, retryReminders);
          }
        },
        "✓ Providers saved",
      );
    }
    setSaving(false);
    if (cardTokenRef.current !== myToken) return; // #664 — skipped mid-save
    goNext();
  };

  const handleBabySizesNext = async () => {
    if (saving || !user) return;
    const kid = babySizeKid;
    if (kid && (diaperSize || clothingSize.trim())) {
      const myToken = cardTokenRef.current;
      setSaving(true);
      // #1479 — ONE RPC PER SIZE, deliberately not one multi-row insert.
      //
      // This was `.insert(rows)` with both sizes in a single statement. Two
      // problems, and the second is why the shape changed rather than just the
      // call: `diaper_size` and `clothing_size` are both named in
      // 20260810000000_family_context_allow_multivalued_keys.sql as keys that
      // STAY single-valued, so a second pass over this card is a 23505 — and
      // because Postgres fails the whole statement, ONE size colliding lost the
      // OTHER size too, even when that one was new and would have inserted fine.
      //
      // Per-size calls mean a collision on one cannot take the other down. The
      // receipt then has to be honest about which ones landed rather than
      // claiming both.
      const sizes: { key: string; value: string; label: string }[] = [];
      if (diaperSize) {
        sizes.push({ key: "diaper_size", value: diaperSize, label: "diaper size" });
      }
      if (clothingSize.trim()) {
        sizes.push({ key: "clothing_size", value: clothingSize.trim(), label: "clothing size" });
      }

      // #1478 — ONE named write per size, shared by the first attempt and the
      // retry. Safe to re-run: upsert_family_fact resolves each size in one
      // statement, so a second attempt after a partial success overwrites the
      // same row rather than making another.
      const writeSize = async (size: { key: string; value: string; label: string }) => {
        const result = await rememberFamilyFact(
          { category: "kid", subject: kid, key: size.key, value: size.value },
          "pro_onboarding",
          "high",
        );
        if (!result.ok) {
          throw Object.assign(
            new Error(
              `upsert_family_fact returned ${result.action ?? "no action row"}${result.error ? `: ${result.error}` : ""}`,
            ),
            { code: result.action ?? "no_action_row", details: result.error },
          );
        }
      };

      const savedLabels: string[] = [];
      const failedSizes: { key: string; value: string; label: string }[] = [];
      let unverifiedSizes = 0;
      for (const size of sizes) {
        try {
          await writeSize(size);
          savedLabels.push(size.label);
        } catch (e) {
          failedSizes.push(size);
          if (isUnverifiedFactWrite(e)) unverifiedSizes += 1;
          reportSetupWrite("baby_sizes", e);
        }
      }
      const anySizeFailed = failedSizes.length > 0;
      if (anySizeFailed) {
        // ⚠️ The retry re-runs ONLY the sizes that failed. Re-running the ones
        // that landed would be harmless against the RPC, but the receipt this
        // prints has to name what the tap actually fixed.
        const retrySizes = [...failedSizes];
        noteWriteFailure(
          "baby_sizes",
          async () => {
            for (const size of retrySizes) await writeSize(size);
          },
          `✓ Baby's ${retrySizes.map((z) => z.label).join(" and ")} saved`,
          // #1857 — "couldn't confirm" only when EVERY failed size is the
          // third state; one proven failure makes the whole step a failure.
          unverifiedSizes === failedSizes.length,
        );
      }

      if (savedLabels.length > 0) {
        if (cardTokenRef.current === myToken) {
          // Names what actually landed when only some did. "Baby's sizes saved"
          // over one saved size would be a receipt for a write that did not
          // happen, which is the thing this whole flow's receipts exist to avoid.
          setSetupReceipt((prev) => [
            ...prev,
            {
              text:
                savedLabels.length === sizes.length
                  ? "✓ Baby's sizes saved"
                  : `✓ Baby's ${savedLabels.join(" and ")} saved`,
            },
          ]);
        }

        // Write 2 (spec §B.11): size_check reminder, only if the box is checked
        // and at least one size actually LANDED. The pre-#1479 code threw on a
        // failed insert and so skipped this entirely, meaning the reminder only
        // ever existed alongside a successful write — gating on savedLabels
        // preserves that rather than scheduling a check against nothing.
        if (sizeCheckReminder) {
          try {
            // #673 — real date, and this is a deep-link target too.
            const { taskId, firstCheckDateIso } = await createSizeCheckTask(user.id, kid, 42, prefs.notifications_timezone);
            if (cardTokenRef.current === myToken) {
              setSetupReceipt((prev) => [
                ...prev,
                { text: `✓ First size check-in: ${formatShortDateUTC(firstCheckDateIso)}`, taskId },
              ]);
              noteCreatedTask(taskId);
            }
            // #1528 — the REMINDER half is retryable now. createSizeCheckTask()
            // used to be a bare insert with no lookup, against a type whose
            // invariant is one row per kid (TasksTab refuses to merge two,
            // saying so). It now looks for this kid's active row and updates it
            // in place, so a second tap re-schedules rather than duplicating.
          } catch (e) {
            reportSetupWrite("baby_sizes", e);
            // ⚠️ Still a non-null retry even though the size FACTS above may
            // have registered their own: noteWriteFailure keeps the FIRST
            // closure for a step, and both halves are individually safe to
            // re-run, so whichever failed first owns the button.
            noteWriteFailure(
              "baby_sizes",
              async () => {
                await createSizeCheckTask(user.id, kid, 42, prefs.notifications_timezone);
              },
              "✓ Size check-in scheduled",
            );
          }
        }
      }
      setSaving(false);
      if (cardTokenRef.current !== myToken) return; // #664 — skipped mid-save
    }
    goNext();
  };

  const handleYouNext = async () => {
    if (saving || !user) return;
    if (aboutMeText.trim()) {
      const myToken = cardTokenRef.current;
      setSaving(true);
      // #1478 — ONE named write, shared by the first attempt and the retry.
      // Extracted rather than copied: two copies of a write is how a retry
      // quietly stops matching the thing it is retrying.
      const writeAboutMe = async () => {
        // #1469 — was a bare .insert(), which threw 23505 on every re-entry.
        // family_context_fact_uidx is unique on
        // (user_id, category, coalesce(subject,''), key) where archived = false
        // and the key is not one of the six #747 multi-valued ones — and
        // about_me is named in that migration as one that STAYS single-valued.
        // The "And what about you?" card is unconditional: Settings -> "Replay
        // Pro setup" (any Pro account since Aug 5 2026) and a second device both
        // replay from card 0 with the input blank, so she retypes over a row
        // that already exists. Her original row survived and the EDIT was the
        // thing dropped, which is the worst half of the trade.
        //
        // NOT fixable with .upsert({ onConflict }): that index is an EXPRESSION
        // index (coalesce(subject,''), not a bare column) with a partial WHERE,
        // and PostgREST's on_conflict takes a plain column list, so Postgres
        // cannot infer it and answers 42P10. Identical reasoning to
        // handlePartnerNext above, which hit this first.
        //
        // upsert_family_fact() resolves it server-side in one statement:
        // about_me is not in the #747 multi-valued set, so it overwrites the one
        // live row and keeps the old text in metadata.previous_value. Atomic
        // where a client-side select-then-write would still race into 23505.
        //
        // Accepted trade (#1469): the RPC stores left(btrim(value), 500), so an
        // about_me over 500 chars truncates where the bare insert stored it
        // whole. This card is a single-line <input> with a short placeholder;
        // Kasandra took the truncation rather than add a maxLength.
        const result = await rememberFamilyFact(
          { category: "self", subject: null, key: "about_me", value: aboutMeText },
          "pro_onboarding",
          "high",
        );
        // The RPC reports refusals as a returned `action`, not a Postgres error,
        // so `ok` is the only honest success signal — checking `error` alone
        // would print the "✓" receipt over a write that never landed (house
        // rule 16). reportSetupWrite() lifts `code`/`details` off the thrown
        // value into Sentry's `extra`, so this carries both.
        if (!result.ok) {
          throw Object.assign(
            new Error(
              `upsert_family_fact returned ${result.action ?? "no action row"}${result.error ? `: ${result.error}` : ""}`,
            ),
            { code: result.action ?? "no_action_row", details: result.error },
          );
        }
      };
      const aboutMeReceipt = "✓ Kin's looking out for you too, not just the crew";
      try {
        await writeAboutMe();
        if (cardTokenRef.current === myToken) {
          setSetupReceipt((prev) => [...prev, { text: aboutMeReceipt }]);
        }
      } catch (e) {
        reportSetupWrite("about_me", e);
        // Safe to re-run: about_me is single-valued and the RPC resolves it in
        // one statement, so a second attempt overwrites the same row. This is
        // the exact write whose 23505 started #1469, and the mom who hit it
        // never went back — the retry is the half that was missing.
        noteWriteFailure("about_me", writeAboutMe, aboutMeReceipt, isUnverifiedFactWrite(e));
      }
      setSaving(false);
      if (cardTokenRef.current !== myToken) return; // #664 — skipped mid-save
    }
    goNext();
  };

  const handleFreeformDone = () => handleBrainDumpNext(freeformText, "✓ Brain dump sorted and saved");

  // ── SMS opt-in card (Issue #153) ──────────────────────────────────────────
  // Same verify handshake as SmsSetupSheet (start_verify -> check_verify) but
  // rendered inline as a §B card. E.164 US only: raw 10 digits -> "+1XXXXXXXXXX".
  const smsE164 = `+1${smsPhoneDigits}`;
  const smsFormattedNumber =
    smsPhoneDigits.length === 10
      ? `(${smsPhoneDigits.slice(0, 3)}) ${smsPhoneDigits.slice(3, 6)}-${smsPhoneDigits.slice(6)}`
      : smsPhoneDigits;

  const callSendSms = async (body: Record<string, unknown>) => {
    const resp = await fetch(SEND_SMS_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token ?? ""}`,
        apikey: EXTERNAL_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify(body),
    });
    const data = await resp.json().catch(() => ({}));
    return { ok: resp.ok, status: resp.status, data };
  };

  const handleSmsSendCode = async () => {
    if (smsPhoneDigits.length !== 10 || !smsConsented || smsSubmitting) return;
    setSmsSubmitting(true);
    setSmsError(null);
    try {
      const { ok, status } = await callSendSms({ action: "start_verify", phone_number: smsE164 });
      if (!ok) {
        setSmsError(
          status === 429
            ? "Give it a minute and tap Resend again 💜"
            : "Hmm, that didn't go through. Try again?"
        );
        return;
      }
      setSmsStep("code");
    } catch {
      setSmsError("Hmm, that didn't go through. Try again?");
    } finally {
      setSmsSubmitting(false);
    }
  };

  const handleSmsResend = async () => {
    setSmsError(null);
    await handleSmsSendCode();
  };

  const handleSmsVerify = async () => {
    if (smsCode.length !== 6 || smsSubmitting) return;
    setSmsSubmitting(true);
    setSmsError(null);
    try {
      const { ok, data } = await callSendSms({ action: "check_verify", phone_number: smsE164, code: smsCode });
      if (!ok || !data?.verified) {
        setSmsError("Hmm, that code didn't match. Try again?");
        return;
      }
      trackFeature("sms_verified");
      // The Edge Function wrote phone_number/phone_verified_at/sms_opted_out
      // server-side (service-role). The patch and a refetch are all we own.
      //
      // Shared with HowKinReachesYou and the Morning Notes chip (#470). In this
      // flow she typically has no rows on yet, which is the case the helper
      // answers by switching Morning Note on by text — exactly what the receipt
      // line below promises. Anything she has already switched off stays off.
      // promised: ["morning"] — this card asks "Want your mornings by text?"
      // and the receipt line below promises exactly that, so morning texts go on
      // even though the row is typically off for a brand-new Pro. Nothing else
      // is touched: the card never mentions task updates or the weekly nudge.
      const patch = buildPostVerifyPatch(prefs, { promised: ["morning"] });
      // #1478 — ONE named write, shared by the first attempt and the retry.
      // Safe to re-run: the patch is computed once above, so a second attempt
      // writes the same values.
      const writeMorningPatch = async () => {
        const e = await updatePreferences(patch);
        if (e) throw e;
      };
      const smsReceipt = `✓ Kin will text your mornings at ${smsFormattedNumber}`;
      let promiseKept = true;
      if (Object.keys(patch).length > 0) {
        try {
          await writeMorningPatch();
        } catch (err) {
          reportSetupWrite("sms_morning_patch", err);
          noteWriteFailure("sms_morning_patch", writeMorningPatch, smsReceipt);
          promiseKept = false;
        }
      }
      refetchPreferences();
      // Deliberately NOT routed into the catch below: the phone verified fine
      // server-side, so "that code didn't match" would be a lie about the one
      // step that worked. Only the morning-text preference failed. She keeps
      // her verified number and moves on — she just isn't promised a text Kin
      // has no row to send. Losing the receipt line is the honest outcome; a
      // "✓ Kin will text your mornings" over an unwritten pref is the exact
      // silent-and-total delivery failure the dispatcher notes warn about.
      if (promiseKept) {
        setSetupReceipt((prev) => [...prev, { text: smsReceipt }]);
      }
      goNext();
    } catch {
      setSmsError("Hmm, that code didn't match. Try again?");
    } finally {
      setSmsSubmitting(false);
    }
  };

  // Skip at either state: write nothing, no receipt line, advance to Close.
  const handleSmsSkip = () => goNext();

  /**
   * #1478 — the tap. Rendered only when the entry actually carries a write that
   * is safe to re-run, so a step with `retry: null` shows its named message and
   * no button rather than a button that does nothing.
   *
   * ⚠️ Disabled while ANY retry is in flight, not just this one. Two taps is how
   * a phone user produces two concurrent writes onto the same family_context
   * row, which is the 23505 race #1483 and #1484 are about.
   */
  const renderRetryButton = (entry: FailedStep) =>
    entry.retry ? (
      <button
        type="button"
        onClick={() => runRetry(entry)}
        disabled={retryingStep !== null}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          marginTop: 4,
          cursor: retryingStep !== null ? "default" : "pointer",
          color: "#54525C",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 13,
          fontWeight: 600,
          textDecoration: "underline",
          opacity: retryingStep !== null ? 0.5 : 1,
        }}
      >
        {retryingStep === entry.step ? RETRYING_LABEL : RETRY_LABEL}
      </button>
    ) : null;

  const renderCounter = () =>
    currentCard.id !== "close" && (
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: "#54525C" }}>
          {stepIndex + 1} of {cards.length}
        </span>
      </div>
    );

  // Issue #665 — the user-visibility half of #416/#541's reportSetupWrite.
  // Non-blocking and dismissible: she can ignore it and keep moving, same as
  // every other card in this flow. Scoped to exactly one card by
  // failureNoticeStep === stepIndex (see the state declaration above) — no
  // separate "already seen" bookkeeping needed, it just stops matching once
  // she advances again. Can appear on the Close card too, alongside its own
  // summary line, if the very last content card's write is what failed —
  // accepted overlap rather than special-cased away; both statements are
  // true and neither contradicts the other.
  const renderFailureNotice = () =>
    failureNoticeStep === stepIndex && (
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 8,
          padding: "10px 12px",
          borderRadius: 10,
          background: "rgba(80,80,200,0.08)",
          marginBottom: 14,
        }}
      >
        <div style={{ flex: 1 }}>
          <p
            className="font-body"
            aria-live="polite"
            style={{ fontSize: 13, color: "#54525C", margin: 0, lineHeight: 1.4 }}
          >
            {/* #1469 — names the field. "That last one" was true and useless:
                the mom who hit the about_me collision had no way to know WHAT
                to go re-enter, so she didn't.

                #1478 — and once she taps, the line says what the tap DID. A
                retry with no visible outcome is the same silence one step
                further on: she would have no way to tell a save from a
                no-op (house rule 13). */}
            {retryOutcome && retryOutcome.step === lastFailedStep
              ? retryOutcome.landed
                ? retrySucceededMessage(lastFailedStep)
                : retryFailedMessage(lastFailedStep, retryOutcome.unverified)
              : inlineFailureMessage(
                  lastFailedStep,
                  failedSteps.find((f) => f.step === lastFailedStep)?.unverified ?? false,
                )}
          </p>
          {/* Gone once it landed: there is nothing left to retry, and a button
              under "saved 💜" invites her to write it a second time. */}
          {!(retryOutcome?.step === lastFailedStep && retryOutcome.landed) &&
            (() => {
              const entry = failedSteps.find((f) => f.step === lastFailedStep);
              return entry ? renderRetryButton(entry) : null;
            })()}
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => setFailureNoticeStep(null)}
          style={{
            display: "flex",
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            color: "#54525C",
            flexShrink: 0,
          }}
        >
          <X size={16} />
        </button>
      </div>
    );

  // Issue #664 — reserves a fixed-height slot ABOVE the buttons so the
  // saving indicator appearing/disappearing never shifts card layout
  // (opacity toggles, the space is always there).
  const renderSavingIndicator = () => (
    <div
      aria-live="polite"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        height: 20,
        marginBottom: 8,
        opacity: saving ? 1 : 0,
        transition: "opacity 0.15s ease",
      }}
    >
      <Loader2 size={13} className="animate-spin" style={{ color: "#5050C8" }} />
      <span className="font-body" style={{ fontSize: 12, fontWeight: 600, color: "#5050C8" }}>
        Kin's writing this down...
      </span>
    </div>
  );

  const renderNextSkipRow = (onNext: () => void, nextDisabled = false) => (
    <>
      {renderSavingIndicator()}
      <button onClick={onNext} disabled={saving || nextDisabled} style={primaryButtonStyle(saving || nextDisabled)}>
        Next
      </button>
      {/* Skip is deliberately NOT disabled by `saving` (#664) — she must
          always be able to bail out of a card, including mid-write. The
          in-flight write isn't cancelled; goNext()'s cardTokenRef bump makes
          every handler ignore its own result once she's moved on, so this
          can't double-advance or grow a receipt line for a card she skipped. */}
      <button onClick={goNext} style={secondaryButtonStyle}>
        Skip
      </button>
    </>
  );

  const renderBody = () => {
    switch (currentCard.id) {
      case "welcome":
        return (
          <>
            <p style={titleStyle}>You're in! Kin Pro unlocked 💜</p>
            <p style={bodyStyle}>
              Here's the big change: Kin remembers now. Anything you tell Kin, in here or just
              mid-chat, she keeps and actually uses.
              <br />
              <br />
              Want to give Kin a quick head start? Takes about a minute, and everything's
              skippable.
            </p>
            <button onClick={handleWelcomeNext} style={primaryButtonStyle(false)}>
              Let's do it 💜
            </button>
            <button onClick={handleFullSkip} style={linkButtonStyle}>
              I'll do this later
            </button>
          </>
        );

      case "zip": {
        // Prefilled means she's already given Kin this once — so don't ask like
        // it's news. Confirming beats re-answering (#533).
        const zipAlreadyKnown = resolveSavedZip(prefs) !== null;
        return (
          <>
            <p style={titleStyle}>{zipAlreadyKnown ? "Still your zip code?" : "First, what's your zip code?"}</p>
            <p style={bodyStyle}>So local ideas are actually local. Never shared, and you can change it anytime in Settings.</p>
            <input
              type="text"
              inputMode="numeric"
              maxLength={5}
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
              style={inputStyle}
            />
            {renderNextSkipRow(handleZipNext)}
          </>
        );
      }

      case "kid_names":
        return (
          <>
            <p style={titleStyle}>Who's in your crew?</p>
            <p style={bodyStyle}>
              Add your kids so Kin can color-code their stuff on your schedule. You can always
              change this later in Family Profile.
            </p>
            {kidNameInputs.map((name, idx) => (
              <div key={idx} style={{ position: "relative" }}>
                <input
                  type="text"
                  value={name}
                  placeholder="Kid's name"
                  aria-label={`Kid name ${idx + 1}`}
                  onChange={(e) =>
                    setKidNameInputs((prev) => prev.map((v, i) => (i === idx ? e.target.value : v)))
                  }
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    e.preventDefault();
                    if (idx === kidNameInputs.length - 1 && name.trim() && kidNameInputs.length < MAX_KIDS) {
                      setKidNameInputs((prev) => [...prev, ""]);
                    } else {
                      handleKidNamesNext();
                    }
                  }}
                  style={{ ...inputStyle, paddingRight: 40 }}
                />
                {(name.trim().length > 0 || kidNameInputs.length > 1) && (
                  <button
                    type="button"
                    aria-label={`Remove kid ${idx + 1}`}
                    onClick={() =>
                      setKidNameInputs((prev) =>
                        prev.length === 1 ? [""] : prev.filter((_, i) => i !== idx)
                      )
                    }
                    style={{
                      position: "absolute",
                      right: 10,
                      top: 12,
                      width: 26,
                      height: 26,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      border: "none",
                      background: "transparent",
                      color: "#54525C",
                      cursor: "pointer",
                    }}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            ))}
            {kidNameInputs.length < MAX_KIDS && (
              <button
                type="button"
                onClick={() => setKidNameInputs((prev) => [...prev, ""])}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  marginBottom: 14,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#5050C8",
                  cursor: "pointer",
                }}
              >
                + Add another
              </button>
            )}
            {renderNextSkipRow(handleKidNamesNext)}
          </>
        );

      case "kid_birthday": {
        const kid = currentCard.kid;
        return (
          <>
            <p style={titleStyle}>When's {kid}'s birthday?</p>
            <p style={bodyStyle}>So Kin can help you plan ahead and never get blindsided by cupcake day 🧁</p>
            <input
              type="date"
              value={kidBirthdayValues[kid] || ""}
              onChange={(e) => setKidBirthdayValues((prev) => ({ ...prev, [kid]: e.target.value }))}
              style={inputStyle}
            />
            {renderNextSkipRow(() => handleKidBirthdayNext(kid))}
          </>
        );
      }

      case "kid_gender": {
        // #1861 — tapping the lit pill clears it, same as Settings and
        // onboarding: a mistap here is otherwise permanent, and a WRONG gender
        // is worse than none (Kin uses the wrong pronoun confidently instead of
        // saying "they").
        const kid = currentCard.kid;
        const picked = kidGenderValues[kid] ?? null;
        return (
          <>
            <p style={titleStyle}>Is {kid} a boy or a girl?</p>
            <p style={bodyStyle}>So Kin says "he" or "she" instead of guessing. Skip it and Kin says "they" 💜</p>
            <div role="group" aria-label={`${kid}: boy or girl`} style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              {KID_GENDER_VALUES.map((g) => {
                const on = picked === g;
                return (
                  <button
                    key={g}
                    type="button"
                    aria-pressed={on}
                    aria-label={`${KID_GENDER_LABELS[g]} — ${kid}`}
                    onClick={() =>
                      setKidGenderValues((prev) => {
                        const next = { ...prev };
                        if (on) delete next[kid];
                        else next[kid] = g;
                        return next;
                      })
                    }
                    className="font-body"
                    style={{
                      padding: "8px 20px",
                      borderRadius: 999,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer",
                      backgroundColor: on ? "#5050C8" : "#FFFFFF",
                      color: on ? "#FFFFFF" : "#1C1A3E",
                      border: `1.5px solid ${on ? "#5050C8" : "#C8C8F4"}`,
                    }}
                  >
                    {KID_GENDER_LABELS[g]}
                  </button>
                );
              })}
            </div>
            {renderNextSkipRow(() => handleKidGenderNext(kid))}
          </>
        );
      }

      case "partner":
        return (
          <>
            <p style={titleStyle}>Is there a partner or co-parent Kin should know about?</p>
            <p style={bodyStyle}>Totally optional. Every family looks different 💜</p>
            <input
              type="text"
              value={partnerName}
              onChange={(e) => setPartnerName(e.target.value)}
              placeholder="Their name"
              style={inputStyle}
            />
            {/* #1861 — the word she uses for them, so Kin says he/she/they
                correctly. Optional; the lit pill clears on re-tap. */}
            <div
              role="group"
              aria-label="Partner: husband, wife, or partner"
              style={{ display: "flex", gap: 8, marginTop: -4, marginBottom: 12, flexWrap: "wrap" }}
            >
              {PARTNER_RELATION_VALUES.map((r) => {
                const on = partnerRelation === r;
                return (
                  <button
                    key={r}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setPartnerRelation(on ? null : r)}
                    className="font-body"
                    style={{
                      padding: "6px 14px",
                      borderRadius: 999,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      backgroundColor: on ? "#5050C8" : "#FFFFFF",
                      color: on ? "#FFFFFF" : "#1C1A3E",
                      border: `1.5px solid ${on ? "#5050C8" : "#C8C8F4"}`,
                    }}
                  >
                    {PARTNER_RELATION_LABELS[r]}
                  </button>
                );
              })}
            </div>
            {renderNextSkipRow(handlePartnerNext)}
          </>
        );

      case "food_rules":
        return (
          <>
            <p style={titleStyle}>Any allergies or food rules in your crew?</p>
            <p style={bodyStyle}>
              Kin bakes these into every meal plan and grocery list automatically. You'll never
              have to repeat them.
            </p>
            <textarea
              value={foodRulesText}
              onChange={(e) => setFoodRulesText(e.target.value)}
              placeholder="e.g. Sarah, peanuts. Carter, no dairy. We do meatless Mondays"
              rows={3}
              style={{ ...inputStyle, resize: "none" }}
            />
            {renderNextSkipRow(() =>
              handleBrainDumpNext(foodRulesText, "✓ Meal plans will respect your food rules automatically")
            )}
          </>
        );

      case "providers": {
        // #672 — last-visit pills, shown only under a checked reminder (no
        // point asking when she's not setting a reminder in the first place).
        const LAST_VISIT_OPTIONS: { key: LastVisitPill; label: string }[] = [
          { key: "recent", label: "Within the last month" },
          { key: "few_months", label: "A few months ago" },
          { key: "over_6mo", label: "Over 6 months ago" },
          { key: "unsure", label: "Not sure" },
        ];
        const renderLastVisitPills = (
          value: LastVisitPill | null,
          onChange: (v: LastVisitPill) => void
        ) => (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: -2, marginBottom: 12, marginLeft: 26 }}>
            <p className="font-body" style={{ width: "100%", fontSize: 12, color: "#54525C", marginBottom: 2 }}>
              When was the last visit?
            </p>
            {LAST_VISIT_OPTIONS.map((opt) => {
              const on = value === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => onChange(opt.key)}
                  className="font-body"
                  style={{
                    padding: "6px 10px",
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    backgroundColor: on ? "#5050C8" : "#FFFFFF",
                    color: on ? "#FFFFFF" : "#1C1A3E",
                    border: `1.5px solid ${on ? "#5050C8" : "#C8C8F4"}`,
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        );
        return (
          <>
            <p style={titleStyle}>Who keeps the crew healthy?</p>
            <p style={bodyStyle}>Just names are fine. Kin can remind you when checkups are due.</p>
            <p style={labelStyle}>Pediatrician</p>
            <input
              type="text"
              value={pediatricianName}
              onChange={(e) => setPediatricianName(e.target.value)}
              style={inputStyle}
            />
            <p style={labelStyle}>Dentist</p>
            <input
              type="text"
              value={dentistName}
              onChange={(e) => setDentistName(e.target.value)}
              style={inputStyle}
            />
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 13, color: "#54525C" }}>
              <input type="checkbox" checked={dentistReminder} onChange={(e) => setDentistReminder(e.target.checked)} />
              Remind me to book the dentist every 6 months
            </label>
            {dentistReminder && renderLastVisitPills(dentistLastVisit, setDentistLastVisit)}
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 13, color: "#54525C" }}>
              <input type="checkbox" checked={pediatricianReminder} onChange={(e) => setPediatricianReminder(e.target.checked)} />
              Remind me to book checkups every year
            </label>
            {pediatricianReminder && renderLastVisitPills(pediatricianLastVisit, setPediatricianLastVisit)}
            {renderNextSkipRow(handleProvidersNext)}
          </>
        );
      }

      case "where_days":
        return (
          <>
            <p style={titleStyle}>Where does everyone spend their days?</p>
            <p style={bodyStyle}>Schools, daycare, the nanny or sitter. Tell Kin once and never get asked which school again.</p>
            <textarea
              value={whereDaysText}
              onChange={(e) => setWhereDaysText(e.target.value)}
              placeholder="e.g. Jason goes to Goodridge Elementary, Blake goes to Green Hills daycare, our sitter is Maggie on Fridays"
              rows={3}
              style={{ ...inputStyle, resize: "none" }}
            />
            {renderNextSkipRow(() => handleBrainDumpNext(whereDaysText, "✓ Schools and caregivers saved"))}
          </>
        );

      case "crews_week":
        return (
          <>
            <p style={titleStyle}>Any sports or activities on repeat?</p>
            <p style={bodyStyle}>Practices, lessons, carpool days. Kin plans your week around them.</p>
            <textarea
              value={crewsWeekText}
              onChange={(e) => setCrewsWeekText(e.target.value)}
              placeholder="e.g. Carter, soccer Tue and Thu. Sarah, swim Saturdays"
              rows={3}
              style={{ ...inputStyle, resize: "none" }}
            />
            {renderNextSkipRow(() => handleBrainDumpNext(crewsWeekText, "✓ Activities saved. Kin plans around them now"))}
          </>
        );

      case "baby_sizes": {
        const needsKidSelect = knownKids.length > 1;
        const nextDisabled = needsKidSelect && !babySizeKid;
        return (
          <>
            <p style={titleStyle}>What size is baby in right now?</p>
            <p style={bodyStyle}>Kin will check in before the blowout phase tells you first 😅</p>
            {needsKidSelect && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                {knownKids.map((kid) => (
                  <button
                    key={kid}
                    onClick={() => setBabySizeKid(kid)}
                    style={{
                      padding: "8px 14px",
                      borderRadius: 999,
                      border: babySizeKid === kid ? "2px solid #5050C8" : "1.5px solid #C8C8F4",
                      background: babySizeKid === kid ? "#5050C8" : "#FFFFFF",
                      color: babySizeKid === kid ? "#FFFFFF" : "#1C1A3E",
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 13,
                    }}
                  >
                    {kid}
                  </button>
                ))}
              </div>
            )}
            <p style={labelStyle}>Diapers</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
              {DIAPER_SIZES.map((size) => (
                <button
                  key={size}
                  onClick={() => setDiaperSize(size)}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    border: diaperSize === size ? "2px solid #5050C8" : "1.5px solid #C8C8F4",
                    background: diaperSize === size ? "#5050C8" : "#FFFFFF",
                    color: diaperSize === size ? "#FFFFFF" : "#1C1A3E",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 13,
                  }}
                >
                  {size}
                </button>
              ))}
            </div>
            <p style={labelStyle}>Clothes</p>
            <input
              type="text"
              value={clothingSize}
              onChange={(e) => setClothingSize(e.target.value)}
              placeholder="e.g. 3-6 months"
              style={inputStyle}
            />
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 13, color: "#54525C" }}>
              <input type="checkbox" checked={sizeCheckReminder} onChange={(e) => setSizeCheckReminder(e.target.checked)} />
              Check in with me about sizes every 6 weeks
            </label>
            {renderNextSkipRow(handleBabySizesNext, nextDisabled)}
          </>
        );
      }

      case "you":
        return (
          <>
            <p style={titleStyle}>And what about you?</p>
            <p style={bodyStyle}>
              A side hustle, a hobby, something you're trying to get back to. Kin's here for that
              too, not just the crew.
            </p>
            <input
              type="text"
              value={aboutMeText}
              onChange={(e) => setAboutMeText(e.target.value)}
              placeholder="e.g. Etsy shop, training for a 10K, trying to read again"
              style={inputStyle}
            />
            {renderNextSkipRow(handleYouNext)}
          </>
        );

      case "freeform":
        return (
          <>
            <p style={titleStyle}>Anything else living rent-free in your brain? 🧠</p>
            <p style={bodyStyle}>Pets, Grandma's birthday, carpool rotation. Dump it, Kin sorts it.</p>
            <textarea
              value={freeformText}
              onChange={(e) => setFreeformText(e.target.value)}
              placeholder={freeformPlaceholder}
              rows={3}
              style={{ ...inputStyle, resize: "none" }}
            />
            {renderSavingIndicator()}
            <button onClick={handleFreeformDone} disabled={saving} style={primaryButtonStyle(saving)}>
              Done
            </button>
            {/* Not disabled by `saving` — see the comment on the Skip button
                in renderNextSkipRow (#664). Same cardTokenRef guard applies. */}
            <button onClick={goNext} style={secondaryButtonStyle}>
              Skip
            </button>
          </>
        );

      case "sms":
        return smsStep === "phone" ? (
          <>
            <p style={titleStyle}>Want your mornings by text? 📱</p>
            <p style={bodyStyle}>
              Kin can text you your morning notes and task updates. Nothing to open, it just shows
              up like a friend would.
            </p>
            <input
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              placeholder="(555) 123-4567"
              value={smsFormattedNumber}
              onChange={(e) => setSmsPhoneDigits(e.target.value.replace(/\D/g, "").slice(0, 10))}
              aria-label="Phone number"
              style={inputStyle}
            />
            {smsError && (
              <p style={{ ...bodyStyle, color: "#DC2626", marginTop: -6, marginBottom: 14 }}>{smsError}</p>
            )}
            <div
              onClick={() => setSmsConsented((v) => !v)}
              style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", userSelect: "none", marginBottom: 8 }}
            >
              <button
                type="button"
                role="checkbox"
                aria-checked={smsConsented}
                aria-labelledby="pro-sms-consent-label"
                onClick={(e) => { e.stopPropagation(); setSmsConsented((v) => !v); }}
                style={{
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 20,
                  height: 20,
                  marginTop: 1,
                  borderRadius: 4,
                  border: "1.5px solid #C8C8F4",
                  backgroundColor: smsConsented ? "#5050C8" : "transparent",
                  cursor: "pointer",
                }}
              >
                {smsConsented && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
              <span
                id="pro-sms-consent-label"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12.5, color: "#54525C", lineHeight: 1.45 }}
              >
                Yes, text me. I agree to receive text notifications from Kin. Message frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out anytime.
              </span>
            </div>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, color: "#9090A8", marginTop: 0, marginBottom: 14, marginLeft: 30 }}>
              <a
                href="https://kinyoudoitall.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { e.preventDefault(); openExternal("https://kinyoudoitall.com/privacy"); }}
                style={{ color: "#5050C8", textDecoration: "underline" }}
              >
                Privacy Policy
              </a>
              {" · "}
              <a
                href="https://kinyoudoitall.com/terms-of-service/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { e.preventDefault(); openExternal("https://kinyoudoitall.com/terms-of-service/"); }}
                style={{ color: "#5050C8", textDecoration: "underline" }}
              >
                Terms
              </a>
            </p>
            <button
              onClick={handleSmsSendCode}
              disabled={smsPhoneDigits.length !== 10 || !smsConsented || smsSubmitting}
              style={primaryButtonStyle(smsPhoneDigits.length !== 10 || !smsConsented || smsSubmitting)}
            >
              {smsSubmitting ? "Sending..." : "Send my code"}
            </button>
            <button onClick={handleSmsSkip} disabled={smsSubmitting} style={secondaryButtonStyle}>
              Skip
            </button>
          </>
        ) : (
          <>
            <p style={titleStyle}>Enter your code</p>
            <p style={bodyStyle}>
              Sent to {smsFormattedNumber}. Didn't get it?{" "}
              <button
                onClick={handleSmsResend}
                disabled={smsSubmitting}
                style={{ background: "none", border: "none", padding: 0, color: "#5050C8", fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 14, fontWeight: 600, textDecoration: "underline", cursor: "pointer" }}
              >
                Resend
              </button>
            </p>
            <input
              type="tel"
              inputMode="numeric"
              placeholder="123456"
              maxLength={6}
              value={smsCode}
              onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              aria-label="6-digit code"
              style={{ ...inputStyle, fontSize: 20, letterSpacing: 4, textAlign: "center" }}
            />
            {smsError && (
              <p style={{ ...bodyStyle, color: "#DC2626", marginTop: -6, marginBottom: 14 }}>{smsError}</p>
            )}
            <button
              onClick={handleSmsVerify}
              disabled={smsCode.length !== 6 || smsSubmitting}
              style={primaryButtonStyle(smsCode.length !== 6 || smsSubmitting)}
            >
              {smsSubmitting ? "Verifying..." : "Verify"}
            </button>
            <button onClick={handleSmsSkip} disabled={smsSubmitting} style={secondaryButtonStyle}>
              Skip
            </button>
          </>
        );

      case "focus_areas": {
        const toggle = (key: string) =>
          setFocusAreas((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
          );
        return (
          <>
            <p style={titleStyle}>What should Kin keep on top of?</p>
            <p style={bodyStyle}>
              Pick as many as you want. Kin puts these first when you're setting things up. You can
              change your mind anytime.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
              {FOCUS_AREAS.map((area) => {
                const on = focusAreas.includes(area.key);
                return (
                  <button
                    key={area.key}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggle(area.key)}
                    className="font-body"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "10px 14px",
                      borderRadius: 999,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: "pointer",
                      backgroundColor: on ? "#5050C8" : "#FFFFFF",
                      color: on ? "#FFFFFF" : "#1C1A3E",
                      border: `1.5px solid ${on ? "#5050C8" : "#C8C8F4"}`,
                    }}
                  >
                    <span aria-hidden>{area.emoji}</span>
                    {area.label}
                  </button>
                );
              })}
            </div>
            {renderNextSkipRow(handleFocusAreasNext)}
          </>
        );
      }

      case "close":
        return (
          <>
            <p style={titleStyle}>That's it. Kin's on it 💜</p>
            {setupReceipt.length === 0 ? (
              <p style={bodyStyle}>No pressure. Mention anything in chat and Kin will remember it from now on.</p>
            ) : (
              <>
                <p style={bodyStyle}>Here's what Kin just set up for you:</p>
                <div style={{ marginBottom: 18 }}>
                  {setupReceipt.map((line, i) => (
                    <p key={i} style={{ ...bodyStyle, marginBottom: 4 }}>
                      {line.text}
                    </p>
                  ))}
                </div>
              </>
            )}
            {/* Issue #665 — session-wide, not scoped to setupReceipt.length: a
                write can fail even when everything else on the receipt
                succeeded, or even if nothing did.

                #1469 REVERSED the second half of #665. That issue deliberately
                did not name the failed field, reasoning that "zip"/"kid_names"
                are internal step names and not a real answer to "what didn't
                save". The first half of that is still true and still honoured:
                the KEYS are never shown, only SETUP_FIELD_LABEL's wording. But
                the vague line was not an answer either, and it is why the mom
                who hit the about_me collision never went back. She was told
                something was missing and not what. */}
            {anyWriteFailed && (
              <div style={{ marginBottom: 6 }}>
                <p style={{ ...bodyStyle, marginBottom: 0 }} aria-live="polite">
                  {summaryFailureMessage(failedSteps.map((f) => ({ step: f.step, unverified: f.unverified })))}
                </p>
                {/* #1478 — one tap per field that is still missing. The Close
                    card is where she actually reads this: the inline notice is
                    scoped to one card and she may well have walked past it.

                    A successful retry removes the step from failedSteps, so the
                    line above rewrites itself and the button goes with it. The
                    receipt line it prints is the confirmation, in the same list
                    as everything else that landed. */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 4 }}>
                  {failedSteps.map((entry) => (
                    <span key={entry.step} style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                      {entry.retry && (
                        <span
                          className="font-body"
                          style={{ fontSize: 13, color: "#54525C" }}
                        >
                          {setupFieldLabel(entry.step)}:
                        </span>
                      )}
                      {renderRetryButton(entry)}
                    </span>
                  ))}
                </div>
                {retryOutcome && !retryOutcome.landed && (
                  <p style={{ ...bodyStyle, marginTop: 4, marginBottom: 0 }} aria-live="polite">
                    {retryFailedMessage(retryOutcome.step, retryOutcome.unverified)}
                  </p>
                )}
              </div>
            )}
            {/* #361 task 6 — name where each captured item actually lives, so
                "change it later" is an instruction rather than a reassurance. */}
            <p style={{ ...bodyStyle, marginBottom: 6 }}>
              Kids, allergies, doctors and food rules live in your Family Profile. Times, texts and
              what Kin sends live in Settings. Change anything, anytime.
            </p>
            {/* #361 task 5 — the flow used to end on "Start chatting", which left a
                mom who just paid with nothing to do next. Scheduled helpers are
                where Pro earns its keep, so that's the primary action. It shows
                whether or not she picked focus areas; only the wording changes.
                #673 — when the flow made a real task, land on it directly rather
                than a generic Tasks tab; firstCreatedTaskId is undefined (not
                just falsy-passed) whenever nothing was created, which is what
                keeps this byte-identical to pre-#673 behavior in that case. */}
            <button
              onClick={() => finishFlow("tasks", firstCreatedTaskId ?? undefined)}
              style={primaryButtonStyle(false)}
            >
              {focusAreas.length > 0 ? "Set these up →" : "See what Kin can run for you →"}
            </button>
            <button onClick={() => finishFlow("chat")} style={secondaryButtonStyle}>
              Start chatting
            </button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center transition-all duration-300"
      style={{
        backgroundColor: visible ? "rgba(28, 26, 62, 0.6)" : "rgba(28, 26, 62, 0)",
        backdropFilter: visible ? "blur(4px)" : "blur(0px)",
      }}
    >
      <div
        className="relative w-full mx-4 overflow-y-auto transition-all duration-400 ease-out"
        style={{
          maxWidth: 440,
          maxHeight: "90vh",
          borderRadius: 20,
          padding: "28px 20px",
          backgroundColor: "#F0F0FF",
          boxShadow: "0 24px 60px rgba(28, 26, 62, 0.25)",
          opacity: visible ? 1 : 0,
          transform: visible ? "scale(1)" : "scale(0.95)",
        }}
      >
        {renderCounter()}
        {renderFailureNotice()}
        {renderBody()}
      </div>
    </div>
  );
};

export default ProSetupFlowModal;
